/* ─── BadgeDisplay — profile badge row ────────────────────────────── */

import { useState, useEffect } from 'react';
import { getUserBadges } from '../services/badgeService';
import { BADGE_DEFINITIONS, BADGE_ORDER } from '../types/social';
import { getFeatherIcon } from '../utils/iconMap';
import type { UserBadges } from '../types/social';

interface BadgeDisplayProps {
  userId: string;
  /** compact = small inline row; full = larger with labels */
  variant?: 'compact' | 'full';
}

export default function BadgeDisplay({ userId, variant = 'compact' }: BadgeDisplayProps) {
  const [badges, setBadges] = useState<UserBadges | null>(null);
  const [tooltip, setTooltip] = useState<string | null>(null);

  useEffect(() => {
    if (!userId) return;
    getUserBadges(userId).then(setBadges).catch(console.error);
  }, [userId]);

  if (!badges || Object.keys(badges.badges).length === 0) return null;

  function handleBadgeClick(badgeId: string) {
    setTooltip(tooltip === badgeId ? null : badgeId);
    // Auto-dismiss after 3s
    setTimeout(() => setTooltip((cur) => (cur === badgeId ? null : cur)), 3000);
  }

  return (
    <div className={`badge-display ${variant}`}>
      {BADGE_ORDER.map((id) => {
        const userBadge = badges.badges[id];
        const def = BADGE_DEFINITIONS[id];
        if (!def) return null;
        const earned = userBadge?.earned;

        return (
          <div key={id} className="badge-item-wrap">
            <button
              className={`badge-item ${earned ? 'earned' : 'unearned'}`}
              onClick={() => handleBadgeClick(id)}
              title={def.title}
            >
              {(() => { const Icon = getFeatherIcon(def.icon); return <Icon size={16} />; })()}
            </button>
            {tooltip === id && (
              <div className="badge-tooltip">
                <strong>{def.title}</strong>
                <span>{def.description}</span>
                {!earned && <span className="badge-tooltip-locked">not yet earned</span>}
              </div>
            )}
          </div>
        );
      })}
      {badges.totalBadges > 0 && variant === 'full' && (
        <span className="badge-count">{badges.totalBadges} badge{badges.totalBadges !== 1 ? 's' : ''} earned</span>
      )}
    </div>
  );
}
